import {
  collectionGroup,
  getDocs,
  getDoc,
  doc,
  query,
  orderBy,
  limit,
  startAfter,
  updateDoc,
  serverTimestamp,
  onSnapshot
} from 'firebase/firestore';
import { firestore } from './firebase/firebase';
import { functions } from './firebase/firebase';
import { httpsCallable } from 'firebase/functions';

/**
 * Service de gestion des demandes de création assistée (côté admin)
 * Les demandes sont stockées dans users/{userId}/creation_request/creation_form
 * (même projet Firebase que le portail).
 */

// Statuts alignés sur le portail
export const REQUEST_STATUS = {
  BROUILLON: 'brouillon',
  NOUVELLE: 'nouvelle',
  CREATION_EN_COURS: 'creation_en_cours',
  VERIFICATION: 'verification',
  SITE_PRET: 'site_pret',
  SITE_PUBLIE: 'site_publié',
  EMAIL_ENVOYE: 'email_envoye'
};

export const REQUEST_STATUS_LABELS = {
  [REQUEST_STATUS.BROUILLON]: 'Brouillon',
  [REQUEST_STATUS.NOUVELLE]: 'Demande reçue',
  [REQUEST_STATUS.CREATION_EN_COURS]: 'Création en cours',
  [REQUEST_STATUS.VERIFICATION]: 'Vérification',
  [REQUEST_STATUS.SITE_PRET]: 'Site prêt',
  [REQUEST_STATUS.SITE_PUBLIE]: 'Site publié',
  [REQUEST_STATUS.EMAIL_ENVOYE]: 'Livré'
};

// Ordre de progression des statuts (pour le sélecteur admin)
// NB : email_envoye supprimé (Étape 10) — site_pret est le statut terminal.
// Les emails au client (confirmation, création, site prêt) sont envoyés AUTOMATIQUEMENT
// par le trigger onCreationRequestStatusChange (plus d'action manuelle).
export const REQUEST_STATUS_ORDER = [
  REQUEST_STATUS.NOUVELLE,
  REQUEST_STATUS.CREATION_EN_COURS,
  REQUEST_STATUS.VERIFICATION,
  REQUEST_STATUS.SITE_PRET,
  REQUEST_STATUS.SITE_PUBLIE
];

const getFormRef = (userId) => doc(firestore, 'users', userId, 'creation_request', 'creation_form');

// Identifiant unique simple pour les previews (évite les collisions d'URL)
const makeId = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`);

const getUserInfo = async (userId) => {
  try {
    const snap = await getDoc(doc(firestore, 'users', userId));
    const data = snap.exists() ? snap.data() : null;
    const displayName = data?.firstName && data?.lastName
      ? `${data.firstName} ${data.lastName}`
      : data?.username || 'Utilisateur';
    return { email: data?.email || 'Email non disponible', displayName };
  } catch (error) {
    console.error('creationRequestService: erreur userInfo:', error);
    return { email: 'Email non disponible', displayName: 'Utilisateur' };
  }
};

const formatRequest = (userId, data) => ({
  userId,
  ...data,
  createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
  updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt,
  submittedAt: data.submittedAt?.toDate ? data.submittedAt.toDate() : data.submittedAt,
  previews: Array.isArray(data.previews) ? data.previews : [],
  messages: Array.isArray(data.messages) ? data.messages : [],
  domainWish: typeof data.domainWish === 'string' ? data.domainWish : '',
  // Identité visuelle souhaitée (couleurs de charte, saisies à l'étape 5)
  design: {
    primaryColor: typeof data.design?.primaryColor === 'string' ? data.design.primaryColor : '',
    secondaryColor: typeof data.design?.secondaryColor === 'string' ? data.design.secondaryColor : ''
  },
  faqAnswers: Array.isArray(data.faqAnswers) ? data.faqAnswers : [],
  domainSuggestions: Array.isArray(data.domainSuggestions) ? data.domainSuggestions : []
});

/**
 * Récupère toutes les demandes (collectionGroup à travers tous les utilisateurs)
 * NB : l'émulateur ne matche que les collections à 2 niveaux → on cible la
 * collection « creation_request » (le document creation_form en fait partie).
 * @param {Object} options - { limit, lastVisible }
 * @returns {Promise<{requests: Array, lastVisible: Object|null}>}
 */
export const getAllCreationRequests = async (options = {}) => {
  try {
    const requestsRef = collectionGroup(firestore, 'creation_request');
    let requestsQuery = query(requestsRef, orderBy('updatedAt', 'desc'));
    if (options.lastVisible) {
      requestsQuery = query(requestsQuery, startAfter(options.lastVisible));
    }
    requestsQuery = query(requestsQuery, limit(options.limit || 100));

    const snapshot = await getDocs(requestsQuery);
    const requests = [];
    for (const reqDoc of snapshot.docs) {
      // Ne conserver que le document de demande « creation_form »
      if (reqDoc.id !== 'creation_form') continue;
      const userId = reqDoc.ref.path.split('/')[1];
      const userInfo = await getUserInfo(userId);
      requests.push({ id: reqDoc.id, ...formatRequest(userId, reqDoc.data()), userInfo });
    }

    return {
      requests,
      lastVisible: snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null
    };
  } catch (error) {
    console.error('creationRequestService: erreur getAll:', error);
    throw error;
  }
};

/**
 * Récupère une demande précise
 */
export const getCreationRequest = async (userId) => {
  try {
    const snap = await getDoc(getFormRef(userId));
    if (!snap.exists()) return null;
    const userInfo = await getUserInfo(userId);
    return { id: snap.id, ...formatRequest(userId, snap.data()), userInfo };
  } catch (error) {
    console.error('creationRequestService: erreur getById:', error);
    throw error;
  }
};

/**
 * Observe une demande en temps réel (messages, statut, previews mis à jour instantanément)
 * @param {string} userId - ID de l'utilisateur de la demande
 * @param {Function} callback - (data, error)
 * @returns {Function} Fonction de désabonnement
 */
export const observeCreationRequest = (userId, callback) => {
  try {
    const unsubscribe = onSnapshot(getFormRef(userId), async (snap) => {
      try {
        if (!snap.exists()) {
          callback(null, new Error('Demande introuvable'));
          return;
        }
        const userInfo = await getUserInfo(userId);
        callback({ id: snap.id, ...formatRequest(userId, snap.data()), userInfo });
      } catch (error) {
        callback(null, error);
      }
    });
    return unsubscribe;
  } catch (error) {
    callback(null, error);
    return () => {};
  }
};

/**
 * Change le statut de la demande (l'admin)
 */
export const updateRequestStatus = async (userId, status) => {
  await updateDoc(getFormRef(userId), { status, updatedAt: serverTimestamp() });
};

/**
 * Ajoute une prévisualisation (URL + libellé)
 * Note : createdAt est une Date JS (sentinelles interdites dans les tableaux Firestore)
 * Chaque preview reçoit un id unique (le marquage « finale » se fait par id, pas par URL).
 */
export const addPreview = async (userId, preview) => {
  const snap = await getDoc(getFormRef(userId));
  const existing = Array.isArray(snap.data()?.previews) ? snap.data().previews : [];
  await updateDoc(getFormRef(userId), {
    previews: [...existing, { id: makeId(), url: preview.url, label: preview.label || '', createdAt: new Date() }],
    updatedAt: serverTimestamp()
  });
};

/**
 * Supprime une prévisualisation par index
 */
export const removePreview = async (userId, index) => {
  const snap = await getDoc(getFormRef(userId));
  const previews = Array.isArray(snap.data()?.previews) ? snap.data().previews : [];
  previews.splice(index, 1);
  await updateDoc(getFormRef(userId), { previews, updatedAt: serverTimestamp() });
};

// ------------------------------------------------------------------
// Domaines recommandés (posés par l'équipe, affichés au client à site_pret)
// Règle métier : stockage { name, extension } SÉPARÉS — le nom complet
// n'est construit qu'à l'affichage (formatDomainSuggestion).
// ------------------------------------------------------------------
export const DOMAIN_SUGGESTION_LIMIT = 3;
const ALLOWED_DOMAIN_EXTENSIONS = ['fr', 'com'];

/**
 * Normalise une suggestion { name, extension } : minuscules, sans « www. »,
 * extension sans point initial. Retourne null si invalide.
 */
const normalizeSuggestion = (input = {}) => {
  const name = String(input.name || '')
    .trim()
    .toLowerCase()
    .replace(/^www\./, '');
  const extension = String(input.extension || '')
    .trim()
    .replace(/^\./, '')
    .toLowerCase();
  if (!name || !ALLOWED_DOMAIN_EXTENSIONS.includes(extension)) return null;
  if (!/^[a-z0-9][a-z0-9-]*$/.test(name)) return null;
  return { name, extension };
};

/**
 * Affiche une suggestion { name, extension } sous forme « monsite.fr ».
 * @param {Object|null} suggestion
 * @returns {string}
 */
export const formatDomainSuggestion = (suggestion) => {
  if (!suggestion || typeof suggestion !== 'object') return '';
  const name = String(suggestion.name || '').trim();
  const extension = suggestion.extension
    ? String(suggestion.extension).replace(/^\./, '').trim()
    : '';
  return extension ? `${name}.${extension}` : name;
};

/**
 * Ajoute une suggestion de domaine (max 3, pas de doublon).
 * @param {string} userId - ID de l'utilisateur de la demande
 * @param {{ name: string, extension: string }} suggestion
 */
export const addDomainSuggestion = async (userId, suggestion) => {
  const snap = await getDoc(getFormRef(userId));
  const existing = Array.isArray(snap.data()?.domainSuggestions) ? snap.data().domainSuggestions : [];

  const clean = normalizeSuggestion(suggestion);
  if (!clean) {
    throw new Error('Nom de domaine invalide (lettres, chiffres, tirets) ou extension non prise en charge (.fr/.com).');
  }
  if (existing.length >= DOMAIN_SUGGESTION_LIMIT) {
    throw new Error(`${DOMAIN_SUGGESTION_LIMIT} domaines maximum par demande.`);
  }
  const duplicate = existing.some(
    (s) => s && s.name === clean.name && (s.extension || '') === clean.extension
  );
  if (duplicate) {
    throw new Error('Ce domaine est déjà dans la liste.');
  }

  await updateDoc(getFormRef(userId), {
    domainSuggestions: [...existing, clean],
    updatedAt: serverTimestamp()
  });
  return clean;
};

/**
 * Supprime une suggestion de domaine par index.
 */
export const removeDomainSuggestion = async (userId, index) => {
  const snap = await getDoc(getFormRef(userId));
  const suggestions = Array.isArray(snap.data()?.domainSuggestions) ? snap.data().domainSuggestions : [];
  suggestions.splice(index, 1);
  await updateDoc(getFormRef(userId), {
    domainSuggestions: suggestions,
    updatedAt: serverTimestamp()
  });
};

/**
 * Définit (ou retire) la preview finale — identifiée par son id (rétrocompat : par URL)
 * Met à jour finalPreviewUrl (utilisé par le portail) + finalPreviewId (marquage admin).
 * @param {string} userId - ID de l'utilisateur de la demande
 * @param {string} idOrUrl - id de la preview (ou son URL pour les anciennes données)
 */
export const setFinalPreviewUrl = async (userId, idOrUrl) => {
  const snap = await getDoc(getFormRef(userId));
  const previews = Array.isArray(snap.data()?.previews) ? snap.data().previews : [];
  const target = previews.find((p) => p.id === idOrUrl) || previews.find((p) => p.url === idOrUrl);
  await updateDoc(getFormRef(userId), {
    finalPreviewUrl: target ? target.url : null,
    finalPreviewId: target && target.id ? target.id : null,
    updatedAt: serverTimestamp()
  });
};

/**
 * Réponse de l'équipe dans le dialogue centralisé de la demande
 */
export const addStaffMessage = async (userId, content, staffName = 'Équipe MonSiteDirect') => {
  const snap = await getDoc(getFormRef(userId));
  const existing = Array.isArray(snap.data()?.messages) ? snap.data().messages : [];
  await updateDoc(getFormRef(userId), {
    messages: [...existing, {
      content,
      isStaff: true,
      authorId: 'admin',
      authorName: staffName,
      timestamp: new Date()
    }],
    updatedAt: serverTimestamp()
  });
};

/**
 * Email « Vous avez reçu un nouveau message de notre équipe » (déclenché manuellement)
 * @param {string} userId - ID de l'utilisateur de la demande
 */
export const sendStaffMessageEmail = async (userId) => {
  const fn = httpsCallable(functions, 'sendStaffMessageEmail');
  const res = await fn({ userId });
  return res.data;
};

/**
 * Email de demande d'avis (feedback) — déclenché manuellement
 * @param {string} userId - ID de l'utilisateur de la demande
 */
export const sendFeedbackRequest = async (userId) => {
  const fn = httpsCallable(functions, 'sendFeedbackRequest');
  const res = await fn({ userId });
  return res.data;
};

